import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { resolveAssetUrl } from "../../utils/api";
import MainCard from "components/MainCard";
import PageHeader from "components/PageHeader";
import SearchField from "components/SearchField";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import StorefrontIcon from "@mui/icons-material/Storefront";
import ScrollableTableContainer from "components/table/ScrollableTableContainer";
import TableActionButton from "components/table/TableActionButton";
import TableActions from "components/table/TableActions";
import TableActionCell from "components/table/TableActionCell";
import ClickableTableRow, { stopRowClickPropagation } from "components/table/ClickableTableRow";
import TableStatusChip from "components/table/TableStatusChip";
import StoreDetailsDialog from "./StoreDetailsDialog";
import { busyChipSx } from "components/table/tableChipStyles";
import { useToast } from "contexts/ToastContext";

import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Button,
  Typography,
  Chip,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  Tooltip,
  Paper,
} from "@mui/material";

const columns = [
  { label: "Store", minWidth: 200 },
  { label: "Logo", minWidth: 72, align: "center" },
  { label: "Type", minWidth: 120 },
  { label: "Contact", minWidth: 130 },
  { label: "Rating", minWidth: 72, align: "center" },
  { label: "Busy", minWidth: 80, align: "center" },
  { label: "Status", minWidth: 90, align: "center" },
  { label: "Actions", minWidth: 120, align: "center" },
];

export default function StoreList() {
  const navigate = useNavigate();
  const { showToast, showApiError } = useToast();
  const [stores, setStores] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [detailStore, setDetailStore] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null, title: "" });
  const [deleting, setDeleting] = useState(false);
  const [lightbox, setLightbox] = useState(null);

  const loadStores = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/store/list");
      const data = Array.isArray(res.data?.data) ? res.data.data : [];
      setStores(data);
      setFiltered(data);
    } catch {
      setStores([]);
      setFiltered([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStores();
  }, [loadStores]);

  useEffect(() => {
    const q = search.toLowerCase().trim();
    if (!q) {
      setFiltered(stores);
      return;
    }
    setFiltered(
      stores.filter((s) =>
        [
          s.title_en,
          s.title_ar,
          s.store_code,
          s.primary_contact,
          s.secondary_contact,
          s.StoreType?.type_name,
          s.BranchGroup?.franchise_name,
        ].some((v) => v?.toLowerCase().includes(q))
      )
    );
  }, [search, stores]);

  const handleDeleteConfirm = async () => {
    setDeleting(true);
    try {
      await api.delete(`/store/delete/${deleteDialog.id}`);
      setStores((prev) => prev.filter((s) => s.store_id !== deleteDialog.id));
      setDeleteDialog({ open: false, id: null, title: "" });
    } catch (err) {
      showApiError(err, "Delete failed");
    } finally {
      setDeleting(false);
    }
  };

  const handleToggleStatus = async (store) => {
    try {
      await api.patch(`/store/${store.store_id}/toggle-status`);
      const next = !store.status;
      const patch = (s) => (s.store_id === store.store_id ? { ...s, status: next } : s);
      setStores((prev) => prev.map(patch));
      setDetailStore((prev) => (prev?.store_id === store.store_id ? { ...prev, status: next } : prev));
    } catch (err) {
      showApiError(err, "Failed to toggle status");
    }
  };

  const handleToggleBusy = async (store) => {
    try {
      await api.patch(`/store/${store.store_id}/toggle-busy`);
      const next = !store.busy;
      const patch = (s) => (s.store_id === store.store_id ? { ...s, busy: next } : s);
      setStores((prev) => prev.map(patch));
      setDetailStore((prev) => (prev?.store_id === store.store_id ? { ...prev, busy: next } : prev));
    } catch (err) {
      showApiError(err, "Failed to toggle busy");
    }
  };

  const imgUrl = (path) => resolveAssetUrl(path);

  const openDetails = (store) => setDetailStore(store);

  return (
    <Box>
      <PageHeader
        title="Stores"
        subtitle="Manage restaurant locations, delivery settings, and store availability."
        count={filtered.length}
        actionLabel="Add Store"
        actionIcon={AddIcon}
        onAction={() => navigate("/store/create")}
      />

      <SearchField
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search by name"
      />

      <MainCard contentSX={{ p: 0 }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}>
            <CircularProgress />
          </Box>
        ) : filtered.length === 0 ? (
          <Box sx={{ textAlign: "center", p: 6, color: "text.secondary" }}>
            <Typography variant="h6">
              {search ? "No stores match your search" : "No stores found"}
            </Typography>
            {!search && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                sx={{ mt: 2 }}
                onClick={() => navigate("/store/create")}
              >
                Create First Store
              </Button>
            )}
          </Box>
        ) : (
          <ScrollableTableContainer
            component={Paper}
            elevation={0}
            sx={{ maxHeight: "calc(100vh - 260px)" }}
          >
            <Table stickyHeader sx={{ minWidth: 1100 }}>
              <TableHead>
                <TableRow>
                  {columns.map((col) => (
                    <TableCell
                      key={col.label}
                      align={col.align || "left"}
                      sx={{
                        minWidth: col.minWidth,
                        fontWeight: 600,
                        fontSize: "0.72rem",
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                        bgcolor: "grey.50",
                        whiteSpace: "nowrap",
                        borderBottom: "2px solid",
                        borderColor: "divider",
                        py: 1.5,
                      }}
                    >
                      {col.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {filtered.map((store) => (
                  <ClickableTableRow
                    key={store.store_id}
                    sx={{ verticalAlign: "middle" }}
                    onClick={() => openDetails(store)}
                  >
                    <TableCell>
                      <Typography fontWeight={600} variant="body2" noWrap sx={{ maxWidth: 220 }}>
                        {store.title_en || "—"}
                      </Typography>
                      {store.title_ar ? (
                        <Typography variant="caption" color="text.secondary" display="block" noWrap>
                          {store.title_ar}
                        </Typography>
                      ) : null}
                      <Typography variant="caption" color="text.disabled" display="block">
                        {store.store_code || "—"}
                      </Typography>
                    </TableCell>

                    <TableCell align="center">
                      {imgUrl(store.logo) ? (
                        <Tooltip title="View logo">
                          <Avatar
                            src={imgUrl(store.logo)}
                            variant="rounded"
                            sx={{ width: 40, height: 40, cursor: "pointer", mx: "auto" }}
                            onClick={stopRowClickPropagation(() =>
                              setLightbox({
                                src: imgUrl(store.logo),
                                label: `${store.title_en} — Logo`,
                              })
                            )}
                          />
                        </Tooltip>
                      ) : (
                        <Avatar
                          variant="rounded"
                          sx={{ width: 40, height: 40, bgcolor: "primary.lighter", mx: "auto" }}
                        >
                          <StorefrontIcon sx={{ fontSize: 18 }} />
                        </Avatar>
                      )}
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2">{store.StoreType?.type_name || "—"}</Typography>
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2">{store.primary_contact || "—"}</Typography>
                      {store.secondary_contact ? (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {store.secondary_contact}
                        </Typography>
                      ) : null}
                    </TableCell>

                    <TableCell align="center">
                      <Typography variant="body2" fontWeight={600}>
                        {store.rating != null && store.rating !== "" ? store.rating : "—"}
                      </Typography>
                    </TableCell>

                    <TableCell align="center">
                      <Chip
                        label={store.busy ? "Busy" : "Free"}
                        size="small"
                        onClick={stopRowClickPropagation(() => handleToggleBusy(store))}
                        sx={busyChipSx(!!store.busy)}
                      />
                    </TableCell>

                    <TableCell align="center">
                      <TableStatusChip
                        active={!!store.status}
                        onClick={stopRowClickPropagation(() => handleToggleStatus(store))}
                      />
                    </TableCell>

                    <TableActionCell>
                      <TableActions>
                        <TableActionButton
                          action="view"
                          title="View details"
                          onClick={() => openDetails(store)}
                        />
                        <TableActionButton
                          action="edit"
                          title="Edit store"
                          onClick={() => navigate(`/store/edit/${store.store_id}`)}
                        />
                        <TableActionButton
                          action="delete"
                          title="Delete store"
                          onClick={() =>
                            setDeleteDialog({
                              open: true,
                              id: store.store_id,
                              title: store.title_en,
                            })
                          }
                        />
                      </TableActions>
                    </TableActionCell>
                  </ClickableTableRow>
                ))}
              </TableBody>
            </Table>
          </ScrollableTableContainer>
        )}
      </MainCard>

      <StoreDetailsDialog
        open={Boolean(detailStore)}
        store={detailStore}
        imgUrl={imgUrl}
        onClose={() => setDetailStore(null)}
        onEdit={
          detailStore
            ? () => {
                setDetailStore(null);
                navigate(`/store/edit/${detailStore.store_id}`);
              }
            : undefined
        }
      />

      <Dialog open={Boolean(lightbox)} onClose={() => setLightbox(null)} maxWidth="md">
        {lightbox && (
          <>
            <DialogTitle sx={{ pb: 1 }}>{lightbox.label}</DialogTitle>
            <DialogContent sx={{ p: 1 }}>
              <Box
                component="img"
                src={lightbox.src}
                alt={lightbox.label}
                sx={{ width: "100%", maxHeight: "70vh", objectFit: "contain", borderRadius: 1 }}
              />
            </DialogContent>
          </>
        )}
      </Dialog>

      <Dialog
        open={deleteDialog.open}
        onClose={() => !deleting && setDeleteDialog({ open: false, id: null, title: "" })}
      >
        <DialogTitle>Delete Store</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete <strong>{deleteDialog.title}</strong>? This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteDialog({ open: false, id: null, title: "" })}
            disabled={deleting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} /> : <DeleteIcon />}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
